import { isInvalidISODate } from "src/helpers/dateHelper";
import {
  isInvalidAsn,
  isInvalidVendorName,
  isInvalidProductId,
  isInvalidProductName,
  isInvalidQuantity,
  isInvalidString,
  isInvalidFileName
} from "src/validation/stringValidation";
import { isInvalidFileSize } from "src/helpers/fileHelper";
import { FormValidator } from "../formValidator";
import { ValidationError, ValidationErrors } from "src/errors";
import { NonOrderProductCheckInInputField, CheckInUnknownArrivalFormDataKey } from "src/types";

class CheckInUnknownArrivalFormValidator extends FormValidator {
  private static validateAsn(value: unknown): ValidationError<"asn"> | undefined {
    return isInvalidAsn(value) ? new ValidationError("Enter a valid ASN", "asn") : undefined;
  }

  private static validateVendorName(value: unknown): ValidationError<"vendorName"> | undefined {
    return isInvalidVendorName(value) ? new ValidationError("Enter a valid vendor name", "vendorName") : undefined;
  }

  private static validateAdditionalDetails(value: unknown): ValidationError<"additionalDetails"> | undefined {
    return value && isInvalidString(value, 0, 1000)
      ? new ValidationError("Enter valid additional details", "additionalDetails")
      : undefined;
  }

  private static validateReasonForUnknownArrival(
    value: unknown
  ): ValidationError<"reasonForUnknownArrival"> | undefined {
    return isInvalidString(value, 4, 500)
      ? new ValidationError("Enter a valid reason for unknown arrival", "reasonForUnknownArrival")
      : undefined;
  }

  private static validateCheckInImages(value: unknown): ValidationError<"checkInImages"> | undefined {
    if (!Array.isArray(value) || !value.length) {
      return;
    }

    if (value.length > 10) {
      return new ValidationError("Can only upload up to 10 files", "checkInImages");
    }

    for (const arrayValue of value as File[]) {
      if (!arrayValue) {
        return new ValidationError("Upload an image file", "checkInImages");
      }

      if (isInvalidFileSize(arrayValue)) {
        return new ValidationError("A file is too large", "checkInImages");
      }

      if (isInvalidFileName(arrayValue.name)) {
        return new ValidationError("File name is invalid", "checkInImages");
      }
    }

    return undefined;
  }

  private static validateNonOrderProductCheckIns(
    value: unknown
  ): ValidationError<"nonOrderProductCheckIns"> | undefined {
    if (!Array.isArray(value) || !value.length) {
      return new ValidationError("Invalid product check-in", "nonOrderProductCheckIns");
    }

    for (const arrayValue of value) {
      const { productId, productName, receivedQty, receivedUkQty, receivedEuQty, hasWarehouseSplit, expiryDates } =
        arrayValue as NonOrderProductCheckInInputField;

      if (productId && isInvalidProductId(productId)) {
        return new ValidationError(
          `${productId ? `(${productId})` : ""} Product check-in has invalid product ID`,
          "nonOrderProductCheckIns"
        );
      }

      if (expiryDates.some(({ date }) => date && isInvalidISODate(date))) {
        return new ValidationError(
          `(${productId}) Product check-in has invalid date (example format: '01/01/2026')`,
          "nonOrderProductCheckIns"
        );
      }

      if (expiryDates.some(({ qty }) => qty && isInvalidQuantity(qty, 1, 100000))) {
        return new ValidationError(
          `(${productId}) Product check-in has invalid quantity for the given expiry date`,
          "nonOrderProductCheckIns"
        );
      }

      if (hasWarehouseSplit) {
        if (isInvalidQuantity(receivedUkQty?.toString() ?? "", 0, 100000)) {
          return new ValidationError(
            `(${productId}) Product check-in has invalid UK quantity`,
            "nonOrderProductCheckIns"
          );
        }
        if (isInvalidQuantity(receivedEuQty?.toString() ?? "", 0, 100000)) {
          return new ValidationError(
            `(${productId}) Product check-in has invalid EU quantity`,
            "nonOrderProductCheckIns"
          );
        }
        const total = (Number(receivedUkQty) || 0) + (Number(receivedEuQty) || 0);
        if (total < 1) {
          return new ValidationError(
            `(${productId}) Product check-in total quantity must be at least 1`,
            "nonOrderProductCheckIns"
          );
        }
      } else if (isInvalidQuantity(receivedQty, 1, 100000)) {
        return new ValidationError(`(${productId}) Product check-in has invalid quantity`, "nonOrderProductCheckIns");
      }

      if (isInvalidProductName(productName)) {
        return new ValidationError(
          `(${productId}) Product check-in has invalid product name`,
          "nonOrderProductCheckIns"
        );
      }
    }

    return undefined;
  }

  private static readonly validationStrategies: Record<
    CheckInUnknownArrivalFormDataKey,
    (value: unknown) => ValidationError<CheckInUnknownArrivalFormDataKey> | undefined
  > = {
    vendorName: CheckInUnknownArrivalFormValidator.validateVendorName,
    asn: CheckInUnknownArrivalFormValidator.validateAsn,
    reasonForUnknownArrival: CheckInUnknownArrivalFormValidator.validateReasonForUnknownArrival,
    additionalDetails: CheckInUnknownArrivalFormValidator.validateAdditionalDetails,
    checkInImages: CheckInUnknownArrivalFormValidator.validateCheckInImages,
    nonOrderProductCheckIns: CheckInUnknownArrivalFormValidator.validateNonOrderProductCheckIns
  };

  static validateWarehouseCheckInFormData<T extends CheckInUnknownArrivalFormDataKey>(
    formData: Partial<Record<T, unknown>>
  ): ValidationErrors<T> {
    return super.validateFormData(
      formData,
      CheckInUnknownArrivalFormValidator.validationStrategies
    ) as ValidationErrors<T>;
  }
}

export { CheckInUnknownArrivalFormValidator };
